// src/utils/goals.js — goal progress calculations

/**
 * Compute progress percentage (0–100) for a goal.
 * - savings / contribution: currentAmount / targetAmount
 * - debt-payoff: (targetAmount - currentAmount) / targetAmount
 */
export function computeGoalProgress(goal) {
  if (!goal.targetAmount || goal.targetAmount <= 0) return 0;
  if (goal.type === 'debt-payoff') {
    const paid = goal.targetAmount - goal.currentAmount;
    return Math.max(0, Math.min(100, (paid / goal.targetAmount) * 100));
  }
  return Math.max(0, Math.min(100, (goal.currentAmount / goal.targetAmount) * 100));
}

/**
 * Format a target date as a relative string if within 12 months,
 * otherwise as an absolute date.
 */
export function formatTargetDate(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + 'T12:00:00');
  const now    = new Date();
  const diffMs = target - now;
  const diffDays = Math.round(diffMs / 86400000);
  if (diffDays < 0) return 'past target';
  if (diffDays === 0) return 'today';
  if (diffDays <= 30) return `${diffDays}d`;
  const diffMonths = Math.round(diffDays / 30.4);
  if (diffMonths <= 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
  return target.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toLowerCase();
}

/**
 * Compute on-track status for a goal using straight-line pace assumption.
 * Returns { status, progressPct, expectedPct, daysRemaining, pacePerDay, projectedFinishDate }
 *
 * Status buckets:
 *   'complete'        — progress >= 100%
 *   'no-target-date'  — no targetDate set
 *   'ahead'           — progress > expected + 5%
 *   'on-track'        — within ±5% of expected
 *   'behind'          — progress < expected - 5%
 *   'at-risk'         — behind AND target date within 30 days
 */
export function computeGoalStatus(goal, now = new Date()) {
  const progressPct = computeGoalProgress(goal);

  if (progressPct >= 100) return { status: 'complete', progressPct };
  if (!goal.targetDate)   return { status: 'no-target-date', progressPct };

  const created  = new Date(goal.createdAt || goal.targetDate);
  const target   = new Date(goal.targetDate + 'T12:00:00');
  const totalMs  = target - created;
  const elapsedMs = now - created;

  const expectedPct = Math.max(0, Math.min(100, totalMs > 0 ? (elapsedMs / totalMs) * 100 : 0));

  const daysRemaining = Math.max(0, Math.ceil((target - now) / 86400000));
  const daysElapsed   = Math.max(1, Math.ceil(elapsedMs / 86400000));

  const targetAmount  = goal.targetAmount || 0;
  const currentNorm   = goal.type === 'debt-payoff'
    ? targetAmount - (goal.currentAmount || 0)
    : (goal.currentAmount || 0);
  const pacePerDay = currentNorm / daysElapsed;

  const remainingAmount = goal.type === 'debt-payoff'
    ? (goal.currentAmount || 0)
    : targetAmount - (goal.currentAmount || 0);
  const daysToFinish = pacePerDay > 0 ? Math.ceil(remainingAmount / pacePerDay) : Infinity;
  const projectedFinishDate = pacePerDay > 0
    ? new Date(now.getTime() + daysToFinish * 86400000)
    : null;

  const diff = progressPct - expectedPct;
  let status;
  if (diff > 5)                        status = 'ahead';
  else if (diff >= -5)                 status = 'on-track';
  else if (daysRemaining <= 30)        status = 'at-risk';
  else                                 status = 'behind';

  return { status, progressPct, expectedPct, daysRemaining, pacePerDay, projectedFinishDate };
}
